import Loader from 'components/Loader';
import { useFormik } from 'formik';
import useRequest from 'hooks/useRequest';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Button, Col, Form, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { SUBSCRIPTION_STATUS_TYPES } from 'utils/constants';
import { titleCaseIfExists } from 'utils/helper';
import * as Yup from 'yup';

const EditModal = ({ isOpen, toggle, selectedItem }) => {
  const currencyTypes = [
    { label: 'INR', value: 'INR' },
    { label: 'USD', value: 'USD' },
  ];

  const subscriptionStatusTypes = Object.keys(SUBSCRIPTION_STATUS_TYPES)
    ?.filter((e) => e?.toLowerCase() !== 'all')
    ?.map((elem) => ({
      label: titleCaseIfExists(elem),
      value: SUBSCRIPTION_STATUS_TYPES[elem],
    }));

  const [subscriptionPlansList, setSubscriptionPlansList] = useState(null);

  const [getSubscriptionPlansListRequest, { loading: getSubscriptionPlansListLoading }] = useRequest({
    url: '/subscription/admin/subscription-plan/search',
    method: 'POST',
    onSuccess: (data) => {
      setSubscriptionPlansList(
        data?.results?.map((e) => ({
          ...e,
          label: `${e?.name} - (${e?.currency})`,
          value: e?.id,
        }))
      );
    },
    onError: (err) => {
      toast.error(err?.message);
    },
  });

  const [updateSubscriptionPlanRequest, { loading: updateSubscriptionPlanLoading }] = useRequest({
    url: `/subscription/admin/user-subscription/${selectedItem?.id}`,
    method: 'PUT',
    onSuccess: (data) => {
      window.location.reload();
      toast.success('Success! User Subscription Plan has been updated.');
    },
    onError: (err) => {
      toast.error(err?.message);
    },
  });

  const getInitialValues = () => ({
    status: '',
    currency: '',
    startDate: null,
    endDate: null,
    subscriptionPlanId: '',
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(),
    onSubmit: (values) => {
      updateSubscriptionPlanRequest({
        body: {
          status: values?.status,
          currency: values?.currency,
          startDate: values?.startDate ? moment(values?.startDate)?.format('YYYY-MM-DD') : null,
          endDate: values?.endDate ? moment(values?.endDate)?.format('YYYY-MM-DD') : null,
          subscriptionPlan: {
            id: values?.subscriptionPlanId,
          },
        },
      });
    },
    validationSchema: Yup.object({
      currency: Yup.string().required('Please Enter Currency'),
      startDate: Yup.date().required('Please Enter Start Date'),
      endDate: Yup.date().required('Please Enter End Date'),
    }),
  });

  const handleUpdateValues = async () => {
    await getSubscriptionPlansListRequest({
      body: {
        page: 1,
        size: 500,
        sortBy: 'id',
        sortOrder: -1,
      },
    });
    formik.setValues({
      status: selectedItem?.status,
      currency: selectedItem?.subscriptionPlan?.currency,
      startDate: !isEmpty(selectedItem?.startDate) ? new Date(selectedItem?.startDate) : null,
      endDate: !isEmpty(selectedItem?.endDate) ? new Date(selectedItem?.endDate) : null,
      subscriptionPlanId: selectedItem?.subscriptionPlan?.id,
    });
  };

  useEffect(() => {
    if (!isEmpty(selectedItem)) {
      handleUpdateValues();
    }
  }, [selectedItem]);

  return (
    <>
      <Loader isActive={getSubscriptionPlansListLoading || updateSubscriptionPlanLoading} />
      <Modal size="md" centered={true} isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>Update Subscription</ModalHeader>
        <Form onSubmit={formik.handleSubmit}>
          <ModalBody className="p-4">
            <Row>
              <Col md={12}>
                <FormGroup className="mb-4">
                  <Label for="currency" htmlFor="grade" className="form-label ">
                    Currency
                  </Label>
                  <div className="position-relative">
                    <Select
                      styles={{
                        menu: (provided) => ({ ...provided, zIndex: 999 }),
                      }}
                      options={currencyTypes}
                      onChange={(e) => formik.setFieldValue('currency', e?.value)}
                      value={currencyTypes?.find((e) => e?.value === formik?.values?.currency)}
                      onBlur={formik.handleBlur}
                      placeholder="Select Currency..."
                    />
                    {formik.touched.currency && formik.errors.currency ? (
                      <FormFeedback className="d-block">{formik.errors.currency}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup className="mb-4">
                  <Label for="subscriptionPlan" htmlFor="subscriptionPlanId" className="form-label ">
                    Subscription Plan
                  </Label>
                  <div className="position-relative">
                    <Select
                      styles={{
                        menu: (provided) => ({ ...provided, zIndex: 999 }),
                      }}
                      options={subscriptionPlansList}
                      onChange={(e) => formik.setFieldValue('subscriptionPlanId', e?.value)}
                      value={subscriptionPlansList?.find((e) => e?.value === formik?.values?.subscriptionPlanId)}
                      onBlur={formik.handleBlur}
                      placeholder="Select Subscription Plan..."
                    />
                    {formik.touched.subscriptionPlanId && formik.errors.subscriptionPlanId ? (
                      <FormFeedback className="d-block">{formik.errors.subscriptionPlanId}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup className="mb-4">
                  <Label for="startDate" htmlFor="startDate" className="form-label ">
                    Start Date
                  </Label>
                  <div className="position-relative">
                    <DatePicker
                      // minDate={new Date()}
                      className="form-control"
                      selected={formik.values.startDate}
                      onChange={(date) => formik.setFieldValue('startDate', date)}
                      placeholder="Select Start Date..."
                      dateFormat="dd-MM-yyyy"
                    />
                    {formik.touched.startDate && formik.errors.startDate ? (
                      <FormFeedback className="d-block">{formik.errors.startDate}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup className="mb-4">
                  <Label for="endDate" htmlFor="endDate" className="form-label ">
                    End Date
                  </Label>
                  <div className="position-relative">
                    <DatePicker
                      // minDate={new Date()}
                      className="form-control"
                      selected={formik.values.endDate}
                      onChange={(date) => formik.setFieldValue('endDate', date)}
                      placeholder="Select End Date..."
                      dateFormat="dd-MM-yyyy"
                    />
                    {formik.touched.endDate && formik.errors.endDate ? (
                      <FormFeedback className="d-block">{formik.errors.endDate}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup className="mb-4">
                  <Label for="status" htmlFor="status" className="form-label ">
                    Status
                  </Label>
                  <div className="position-relative">
                    <Select
                      styles={{
                        menu: (provided) => ({ ...provided, zIndex: 999 }),
                      }}
                      options={subscriptionStatusTypes}
                      onChange={(e) => formik.setFieldValue('status', e?.value)}
                      value={subscriptionStatusTypes?.find((e) => e?.value === formik?.values?.status)}
                      onBlur={formik.handleBlur}
                      placeholder="Select Status..."
                    />
                    {formik.touched.status && formik.errors.status ? (
                      <FormFeedback className="d-block">{formik.errors.status}</FormFeedback>
                    ) : null}
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button className="me-2" color="primary" type="submit" disabled={updateSubscriptionPlanLoading}>
              Save
            </Button>
            <Button type="button" color="light" onClick={toggle}>
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default EditModal;
